import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box, Button, Menu, MenuItem, useMediaQuery, useTheme} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Close';
import {useConfirm} from 'material-ui-confirm';
import {Link as RouterLink, useParams} from 'react-router-dom';
import Link from '@mui/material/Link';
import PropTypes from 'prop-types';
import CheckIcon from '@mui/icons-material/Check';
import {grey} from '@mui/material/colors';
import {validateEmail, validateFieldLength, validatePhoneNumber} from '../../../../../utility/validationUtil';
import {
    useAddContactMutation,
    useDeleteContactMutation,
    useGetContactsQuery,
    useGetOtherChildrenForEmailChangeQuery,
    useManageConfirmMutation,
    useUpdateContactMutation,
    useUpdateContactPreferencesMutation
} from '../../../../../api/services/profilesService';
import useUser from '../../../../../utility/hooks/useUser';
import ContactEmergencyRoundedIcon from '@mui/icons-material/ContactEmergencyRounded';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import {formatDate} from '../../../../../utility/dateUtil';
import Typography from '@mui/material/Typography';
import {retrieveContactRelations, retrieveCountries, retrieveProfile} from '../../ProfilesSlice';
import AdmicityTableV2 from '../../../../../shared-components/Table/V2/AdmicityTableV2';
import IconButton from '@mui/material/IconButton';
import useNotification from '../../../../../utility/hooks/useNotification';
import HomeIcon from '@mui/icons-material/Home';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditHomeAddressDialog from './EditHomeAddressDialog';
import {useGetContactTitlesQuery} from '../../../../../api/services/lookupsService';
import PROFILE_TYPE from '../../../../../constants/profileTypes';
import Stack from '@mui/material/Stack';
import {
    DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS,
    PROFILE_SECTIONS_KEYS
} from '../../../../../constants/ProfileSectionsKeys';
import {useGetProfileDescriptionQuery} from '../../../../../api/services/schoolManagementService';
import formatProfileDescription from '../../../../../utility/profileDescriptionFormatter';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';
import CONTACT_HELP from './contactHelpText';
import ContactHelpTip, {ContactTooltip, TapTooltip} from './ContactHelpTip';
import AddContactBox from './AddContactBox';
import ContactNotificationsBox from './ContactNotificationsBox';
import {toLookupAddress} from './addContactRules';
import UpdateRequestDialog, {UPDATE_REQUEST_BUTTON} from '../../Components/UpdateRequestDialog';

const isProfileContactEmailRequired = (parentalResponsibility, priority) => {
    if (!parentalResponsibility) {
        return false;
    }

    const parsedPriority = priority == null || priority === '' ? null : Number(priority);
    return parsedPriority === 1 || parsedPriority === 2;
};

/* eslint-disable react/prop-types */

// Maria, 19 October 2026: the table fills the width of the page and never scrolls sideways at a normal browser width.
// Every column is as wide as what is in it, on one line, and Name has the spare room. When the table does not fit, one
// thing at a time gives way, and the next only if it still does not fit: the 'i' icons go under their headings, then the
// Legal Order heading goes onto two lines, then the name wraps, then the email breaks after the @.
// fitStep: 0 one line, 1 icons under the headings, 2 Legal Order on two lines, 3 the name wraps, 4 the email breaks.
const LAST_FIT_STEP = 4;
// once a heading takes two lines, every heading lines up with the top line
const headingAlignment = fitStep => ({
    verticalAlign: fitStep >= 1 ? 'top' : 'middle',
    '& .Mui-TableHeadCell-Content': {width: '100%', alignItems: fitStep >= 1 ? 'flex-start' : 'center'},
    '& .Mui-TableHeadCell-Content-Labels': {alignItems: fitStep >= 1 ? 'flex-start' : 'center'}
});
const SNUG_COLUMNS = ['mrt-row-actions', 'priority', 'parentalResponsibility', 'legalOrder'];
const fitColumn = (column, fitStep) => {
    if (column.id === 'name') {
        return {width: 'auto !important', minWidth: '0 !important', whiteSpace: fitStep >= 3 ? 'normal' : 'nowrap'};
    }
    return {
        width: '1% !important',
        minWidth: '0 !important',
        whiteSpace: column.id === 'email' && fitStep >= 4 ? 'normal' : 'nowrap',
        ...(SNUG_COLUMNS.includes(column.id) ? {px: '8px !important'} : {})
    };
};

// AD-2752 (Maria, 22 October 2026): on a phone the table scrolls sideways. Name stays in view on the left, so it is
// clear whose phone number or email is showing. Name and the email may take two lines; the other columns take one.
// Actions scrolls with the other columns, so there is room to read the email beside the name.
const phoneColumn = column => {
    if (column.id === 'name') {
        return {whiteSpace: 'normal', minWidth: '130px !important', width: '130px !important', maxWidth: 130, position: 'sticky', left: 0, zIndex: 1, borderRight: '1px solid rgba(0, 0, 0, 0.12)'};
    }
    return {
        whiteSpace: column.id === 'email' ? 'normal' : 'nowrap',
        ...(column.id === 'email' ? {minWidth: '0 !important', maxWidth: 190} : {}),
        ...(SNUG_COLUMNS.includes(column.id) ? {px: '8px !important'} : {}),
        ...(column.id === 'mrt-row-actions' ? {position: 'static !important'} : {})
    };
};

const breakAfterAt = value => (typeof value === 'string' ? value.replace('@', '@\u200B') : value);

const renderBooleanCellValue = (value) =>
    value === true
        ? (<CheckIcon fontSize="small" sx={{color: '#2e7d32'}}/>)
        : (<CancelIcon fontSize="small" color="disabled"/>)

// AD-2722: a parent sees why another contact's details are hidden.
const HiddenValue = () => (
    <TapTooltip title={CONTACT_HELP.hidden}>
        <span style={{color: grey[500], cursor: 'help'}} data-testid="contact-hidden">hidden</span>
    </TapTooltip>
);

const hiddenCellFormater = (cell, user) => {
    if (user.isParent() && cell.row.original.userId !== user.id) {
        return <HiddenValue/>;
    }

    const value = cell.getValue();

    if (typeof value === 'boolean') {
        return renderBooleanCellValue(value)
    }

    return value;
};

/* eslint-enable react/prop-types */

// AD-2724: a shared address such as a school office's can be on many children; the first ten are named
const FAMILY_LINKS_SHOWN = 10;

const Contacts = ({readonly}) => {
    // the table is measured after each change, and the next step is taken only while it still runs past the page
    const tableBoxRef = useRef(null);
    const theme = useTheme();
    const isPhone = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    // AD-2752: on a phone the actions are in one '⋯' menu for each contact
    const [actionsMenu, setActionsMenu] = useState({anchor: null, contact: null});
    const closeActionsMenu = () => setActionsMenu({anchor: null, contact: null});
    const [fitStep, setFitStep] = useState(0);
    const checkFit = useCallback(() => {
        const box = tableBoxRef.current;
        // on a phone the table scrolls sideways instead
        if (isPhone) return;
        if (box && fitStep < LAST_FIT_STEP && box.scrollWidth > box.clientWidth + 1) setFitStep(step => step + 1);
    }, [fitStep, isPhone]);
    useLayoutEffect(checkFit);
    useEffect(() => {
        // a wider window may now fit more on one line, so start again from one line
        const onResize = () => setFitStep(0);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const {
        selectedProfile,
        contactRelations,
        currentSchool
    } = useSelector(state => state.profilesInfo);
    const {studentId} = useParams();
    const [openCreateContactDialog, setOpenCreateContactDialog] = useState(false);
    const [openEditHomeAddressDialog, setOpenEditHomeAddressDialog] = useState(false);
    const [openEditContactDialog, setOpenEditContactDialog] = useState(false);
    const [notificationsContact, setNotificationsContact] = useState(null);
    const [selectedContact, setSelectedContact] = useState({});
    const {user} = useUser();
    const confirm = useConfirm();
    const dispatch = useDispatch();
    const [addContact, {isLoading: isAddContactInProgress}] = useAddContactMutation();
    const [updateContact, {isLoading: isUpdateContactInProgress}] = useUpdateContactMutation();
    const [updateContactPreferences, {isLoading: isUpdatePreferencesInProgress}] = useUpdateContactPreferencesMutation();
    const [deleteContact] = useDeleteContactMutation();
    const [manageConfirm] = useManageConfirmMutation();
    const userHasParentRole = user.isParent();
    const {showErrorNotification} = useNotification();
    const isEnquiryProfile = selectedProfile.personType === PROFILE_TYPE.ENQUIRY;

    const {
        data: contacts = {options: []},
        isLoading: isContactsLoading,
    } = useGetContactsQuery({studentId, isParent: userHasParentRole});
    // AD-2723: a parent changing their own email is told which other children's profiles change too
    const {data: otherChildren = []} = useGetOtherChildrenForEmailChangeQuery(
        {contactId: selectedContact?.id},
        {skip: !openEditContactDialog || !userHasParentRole || !selectedContact?.id}
    );

    const {data: titles = []} = useGetContactTitlesQuery();
    // Maria, 29 October 2026 (AD-2817): people with View access to Contacts have their own introduction
    const contactsDescriptionKey = readonly ? PROFILE_SECTIONS_KEYS.contactsViewAccess : PROFILE_SECTIONS_KEYS.contacts;
    // AD-2828 (Maria, 29 and 30 October 2026): a parent with View access can tell the school that contact details need to
    // be updated
    const isViewOnlyParent = Boolean(readonly && userHasParentRole);
    const [openUpdateRequest, setOpenUpdateRequest] = useState(false);
    const {data: contactsDescriptionData} = useGetProfileDescriptionQuery(contactsDescriptionKey);
    const contactsDescription = contactsDescriptionData?.value ??
        DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[contactsDescriptionKey];

    useEffect(() => {
        dispatch(retrieveContactRelations());
        dispatch(retrieveCountries());
    }, []);

    /* eslint-disable react/prop-types */
    const columns = useMemo(() =>
        [
            // Maria, 17 October 2026: every column fits on the screen. The title, forename and surname together, with
            // the relationship underneath, then priority, parental responsibility and legal order, then phone and email.
            {
                id: 'name',
                header: 'Name',
                size: 200,
                grow: false,
                accessorFn: contact => contact.surname,
                Cell: ({row}) => {
                    const contact = row.original;
                    const hidden = userHasParentRole && contact.userId !== user.id;
                    const title = hidden ? '' : titles?.find(e => e.id === contact.titleId)?.description;

                    return (
                        <Box>
                            <Typography sx={{fontWeight: 600, fontSize: 15, lineHeight: 1.25, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                {[title, contact.forename, contact.surname].filter(Boolean).join(' ')}
                                {/* Maria, 18 October 2026: a green tick when the contact has an Admicity account */}
                                {!hidden && contact.userId && (
                                    <ContactTooltip title={CONTACT_HELP.account}>
                                        <CheckCircleIcon sx={{fontSize: 17, color: '#2e7d32'}} aria-label={CONTACT_HELP.account} data-testid="contact-has-account"/>
                                    </ContactTooltip>
                                )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.25}}>
                                {hidden ? <HiddenValue/> : contact.relationType}
                            </Typography>
                            {/* AD-2724 (Maria): the contact's other children at the school, for staff only; a click
                                opens the child's profile */}
                            {!userHasParentRole && contact.familyLinks?.length > 0 && (
                                <Typography
                                    variant="body2"
                                    // AD-2752: on a phone the links wrap, so Name stays narrow while it is kept in view
                                    sx={{lineHeight: 1.35, mt: 0.25, ...(isPhone ? {whiteSpace: 'normal', maxWidth: 130} : {})}}
                                    data-testid="family-links"
                                >
                                    <Box component="span" sx={{color: 'text.secondary'}}>Family Links: </Box>
                                    {contact.familyLinks.slice(0, FAMILY_LINKS_SHOWN).map((link, index) => (
                                        <React.Fragment key={link.studentId}>
                                            {index > 0 && ', '}
                                            <Link component={RouterLink} to={`/profiles/students/${link.studentId}/basic`} underline="hover">
                                                {link.fullName}
                                            </Link>
                                        </React.Fragment>
                                    ))}
                                    {contact.familyLinks.length > FAMILY_LINKS_SHOWN &&
                                        ` and ${contact.familyLinks.length - FAMILY_LINKS_SHOWN} more`}
                                </Typography>
                            )}
                        </Box>
                    );
                }
            },
            {
                accessorKey: 'phoneNumber',
                header: 'Phone Number',
                size: 130,
                grow: false,
                Cell: ({cell}) => hiddenCellFormater(cell, user)
            },
            {
                accessorKey: 'email',
                header: 'Email',
                size: 260,
                grow: true,
                Cell: ({cell}) => (
                    // on a narrower screen the address may break after the @, so the name keeps its room
                    <Box component="span">{breakAfterAt(hiddenCellFormater(cell, user))}</Box>
                )
            },
            // Maria, 19 October 2026: Priority, PR and Legal Order after the email
            ...(selectedProfile.personType !== PROFILE_TYPE.ENQUIRY
                    ? [
                        {
                            accessorKey: 'priority',
                            header: 'Priority',
                            Header: () => <ContactHelpTip label="Priority" text={CONTACT_HELP.priority} stacked={fitStep >= 1}/>,
                            size: 90,
                            grow: false,
                            muiTableHeadCellProps: {align: 'center'},
                            muiTableBodyCellProps: {align: 'center'}
                        }
                    ]
                    : []
            ),
            {
                accessorKey: 'parentalResponsibility',
                header: 'Parental Responsibility',
                // Maria, 18 October 2026: 'PR' in the heading; the pop-up explains Parental Responsibility
                Header: () => <ContactHelpTip label="PR" text={CONTACT_HELP.parentalResponsibility} stacked={fitStep >= 1}/>,
                size: 90,
                grow: false,
                muiTableHeadCellProps: {align: 'center'},
                muiTableBodyCellProps: {align: 'center'},
                Cell: ({cell}) => renderBooleanCellValue(cell.getValue())
            },
            {
                accessorKey: 'legalOrder',
                header: 'Legal Order',
                Header: () => <ContactHelpTip label="Legal Order" text={CONTACT_HELP.legalOrder(selectedProfile.name)} stacked={fitStep >= 1} wrap={fitStep >= 2}/>,
                size: 100,
                grow: false,
                muiTableHeadCellProps: {align: 'center'},
                muiTableBodyCellProps: {align: 'center'},
                Cell: ({cell}) => renderBooleanCellValue(cell.getValue())
            }
        ], [user, titles, selectedProfile.name, fitStep, isPhone]);
    /* eslint-enable react/prop-types */

    const handleEditClick = (contact) => {
        setSelectedContact(contact);
        setOpenEditContactDialog(true);
    };

    const handleEditAddressClick = (contact) => {
        setSelectedContact(contact);
        setOpenEditHomeAddressDialog(true);
    };

    const handleDeleteClick = (contact) => {
        confirm({
            title: 'Confirm Deletion',
            description: (
                <>
                    Are you sure you want to delete this contact?
                    <br /><br />
                    <strong>{contact?.forename} {contact?.surname}</strong>
                </>
            ),
            confirmationText: 'Delete',
            cancellationText: 'Cancel'
        })
            .then(async () => {
                await deleteContact({ studentId, contactId: contact.id });
            })
            .catch(() => {

            });
    };

    const handleDialogClose = () => {
        setOpenCreateContactDialog(false);
        setOpenEditContactDialog(false);
        setOpenEditHomeAddressDialog(false);
        setSelectedContact({});
    };

    const validateContactPayload = (payload) => {
        const priority = payload.priority != null && payload.priority !== ''
            ? parseInt(payload.priority, 10)
            : null;
        const emailRequired = isProfileContactEmailRequired(payload.parentalResponsibility, priority);
        const email = payload.email?.trim() ?? '';
        const phone = payload.phoneNumber?.trim() ?? '';

        if (emailRequired && !email) {
            return {
                isValid: false,
                message: 'Email is required for contacts with parental responsibility at priority 1 or 2.',
                field: 'email',
            };
        }

        if (!emailRequired && !email && !phone) {
            return {
                isValid: false,
                message: 'Phone number is required when email is not provided.',
                field: 'phone',
            };
        }

        if (email) {
            const emailResult = validateUniqueEmail(email, contacts.contacts, selectedContact.email, false);
            if (!emailResult.isValid) {
                return {...emailResult, field: 'email'};
            }
        }

        if (phone) {
            const lengthResult = validateFieldLength(phone, 20, false);
            // AD-2798: the message shows under the Phone box
            if (!lengthResult.isValid) {
                return {...lengthResult, field: 'phone'};
            }

            const phoneResult = validatePhoneNumber(phone);
            if (!phoneResult.isValid) {
                return {...phoneResult, field: 'phone'};
            }
        }

        return {isValid: true, message: ''};
    };

    const handleSaveContact = async (payload) => {
        const validation = validateContactPayload(payload);
        if (!validation.isValid) {
            showErrorNotification(validation.message);
            return;
        }

        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the contact section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(async () => {
                if (openCreateContactDialog) {
                    await addContact({
                        studentId,
                        body: {studentId, ...payload}
                    }).then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));
                } else {
                    await updateContact({
                        studentId,
                        isParent: userHasParentRole,
                        body: {studentId, contact: {...selectedContact, ...payload}}
                    }).then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));
                }

                setSelectedContact({});
                handleDialogClose();
            })
            .catch(() => {
            });
    };

    const handleSaveContactByParent = async (payload) => {
        const validation = validateContactPayload(payload);
        if (!validation.isValid) {
            showErrorNotification(validation.message);
            return;
        }

        return confirm({
            description: 'Please check the new contact information you have provided carefully, as it will import directly into the school’s information management system and you will not be able to view or amend the information in your Admicity parent account after it has been submitted.',
            confirmationText: 'Confirm',
            title: 'Confirmation',
        })
            .then(async () => {
                if (openCreateContactDialog) {
                    await addContact({
                        studentId,
                        isParent: userHasParentRole,
                        body: {studentId, ...payload}
                    }).then(
                        (result) => {
                            if (result.error) {
                                showErrorNotification(result.error.data?.validationErrors.map(x => x.errorMessage));
                                return;
                            }
                            setSelectedContact({});
                            handleDialogClose();
                            dispatch(retrieveProfile({studentId: selectedProfile.id}));
                        }
                    );
                } else {
                    await updateContact({
                        studentId,
                        isParent: userHasParentRole,
                        body: {studentId, contact: {...selectedContact, ...payload}}
                    }).then(
                        (result) => {
                            if (result.error) {
                                showErrorNotification(result.error.data?.validationErrors.map(x => x.errorMessage));
                                return;
                            }
                            dispatch(retrieveProfile({studentId: selectedProfile.id}));
                            setSelectedContact({});
                            handleDialogClose();
                        }
                    );
                }
            })
            .catch(() => {
            });
    };

    const handleSaveContactAddress = async (payload, addressUpdateMode = 'current') => {
        const updateModeText = addressUpdateMode === 'all'
            ? 'This will update the address for all contacts who share the same current address.'
            : 'This will only update the address for this contact.';

        confirm({
            title: 'Confirmation',
            description: (
                <Box>
                    <Typography>
                        {userHasParentRole
                            ? "Please check the new contact address information you have provided carefully, as it will import directly into the school's information management system and you will not be able to view or amend the information in your Admicity parent account after it has been submitted."
                            : `I confirm that the information in the contact section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`
                        }
                    </Typography>
                    <Typography sx={{mt: 2, fontWeight: 'bold'}}>
                        {updateModeText}
                    </Typography>
                </Box>
            ),
            confirmationText: 'Confirm'
        })
            .then(async () => {
                await updateContact({
                    studentId,
                    isParent: userHasParentRole,
                        body: {
                            studentId,
                            contact: {...selectedContact, address: {...payload, country: payload.countryCode}, updateAllLinkedAddresses: addressUpdateMode === 'all'},
                        }
                }).then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));
                setSelectedContact({});
                handleDialogClose();
            })
            .catch(() => {
            });
    };

    const handleConfirm = () => {
        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the contact section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(async () => {
                await manageConfirm({studentId: selectedProfile.id, type: STUDENT_PROFILE_CONFIRMATION_TYPES.Contacts})
                    .then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));
            })
            .catch(() => {
            });
    };

    const validateUniqueEmail = (value, existingContacts, currentEmail, isRequired = true) => {
        const emailValidationResults = validateEmail(value, isRequired);

        if (!emailValidationResults.isValid || value === currentEmail) {
            return emailValidationResults;
        }
        /* eslint-disable */
        const existingContactWithEmail = existingContacts.find(contact =>
            contact.email?.trim().toUpperCase() === value.trim().toUpperCase()
        );
        /* eslint-enable */

        const isValid = !existingContactWithEmail;
        const message = isValid ? '' : 'Contact with the same email already exist';

        return {isValid, message};
    };

    // Maria, 17 October 2026: the house first, larger and green, then 'Edit' and 'Delete' as small buttons.
    const homeAction = contact => ({kind: 'home', label: CONTACT_HELP.viewHomeAddress, onClick: () => handleEditAddressClick(contact)});
    const editAction = contact => ({kind: 'edit', label: 'Edit', onClick: () => handleEditClick(contact)});
    const deleteAction = contact => ({kind: 'delete', label: 'Delete', onClick: () => handleDeleteClick(contact)});
    // AD-2746 (Maria, 21 October 2026): staff change Email Notifications from the envelope, only for a contact who
    // has created an Admicity account. AD-2645: a parent has it on their own contact, with the school's SIMS boxes.
    const notificationsAction = contact => ({kind: 'notifications', label: 'Email Notifications', onClick: () => setNotificationsContact(contact)});
    // a contact without an account keeps the envelope's space, so Edit and Delete line up down the column
    const notificationsActions = contact => [contact.userId ? notificationsAction(contact) : {kind: 'notificationsSpace'}];

    const handleSaveNotifications = async ({emailNotificationsEnabled, simsBoxes}) => {
        const result = await updateContactPreferences({
            studentId,
            contactId: notificationsContact.id,
            // a School Admin who is also a parent uses the staff request
            isParent: !user.hasElevatedAccess(),
            body: {emailNotificationsEnabled, simsBoxes}
        });
        if (result?.error) {
            showErrorNotification(result.error.data?.validationErrors?.map(x => x.errorMessage) ?? 'The change could not be saved.');
            return;
        }
        setNotificationsContact(null);
        dispatch(retrieveProfile({studentId: selectedProfile.id}));
    };

    const getContactActions = (contact) => {
        if (readonly) {
            return isEnquiryProfile ? [homeAction(contact)] : [];
        }

        if (user.hasElevatedAccess()) {
            return [homeAction(contact), ...notificationsActions(contact), editAction(contact), deleteAction(contact)];
        }

        return user.id === contact.userId ? [homeAction(contact), notificationsAction(contact), editAction(contact)] : [];
    };

    return (
        isContactsLoading
            ? ''
            : <>
                {/* Maria, 18 October 2026: the heading is on the line under the tabs, after the student's name; space
                    between the introduction and the table */}
                <Box sx={{mb: 1.5}}>
                    <Typography variant="body1" sx={{whiteSpace: 'pre-line'}}>
                        {formatProfileDescription(
                            contactsDescription,
                            {
                                schoolName: currentSchool.name,
                                studentName: selectedProfile.name,
                                hisHer: getHisHerPronoun(selectedProfile.gender),
                            }
                        )}
                    </Typography>
                    {isViewOnlyParent && (
                        <Button variant="outlined" sx={{mt: 1.5, width: {xs: '100%', sm: 'auto'}}}
                            onClick={() => setOpenUpdateRequest(true)}>{UPDATE_REQUEST_BUTTON}</Button>
                    )}
                </Box>
                {isViewOnlyParent && (
                    <UpdateRequestDialog
                        open={openUpdateRequest}
                        onClose={() => setOpenUpdateRequest(false)}
                        studentId={studentId}
                        section="Contacts"
                        text={`Please tell us which contact details need to be updated. This information will be sent to ${currentSchool.name} so that they can update it.`}
                    />
                )}
                <AdmicityTableV2
                    columns={columns}
                    data={contacts.contacts ?? []}
                    noRowsOverlay={{
                        icon: <ContactEmergencyRoundedIcon/>,
                        text: 'No contacts have been added'
                    }}
                    tableProps={{
                        enableSorting: false,
                        enableColumnResizing: false,
                        enableBottomToolbar: false,
                        enableTopToolbar: false,
                        enablePagination: false,
                        enableStickyHeader: false,
                        enableStickyFooter: false,
                        enableRowActions: (readonly && isEnquiryProfile) || !readonly,
                        muiTablePaperProps: {
                            variant: 'outlined',
                            elevation: 0,
                            sx: {
                                display: 'block',
                                flexGrow: 0,
                                width: '100%',
                                height: 'auto',
                            }
                        },
                        muiTableContainerProps: {
                            ref: tableBoxRef,
                            sx: {
                                flex: 'unset',
                                // Maria, 18 October 2026: every contact shows on the page, and the columns shrink to fit
                                // the width, so the table never scrolls sideways or inside itself
                                // AD-2752: on a phone it scrolls sideways, so every column can be reached
                                overflowX: isPhone ? 'auto' : 'hidden',
                                overflowY: 'auto',
                            }
                        },
                        muiTableBodyProps: {
                            sx: {
                                '& :last-child td': {
                                    borderBottom: 0,
                                },
                            }
                        },
                        muiTableBodyRowProps: {hover: false},
                        layoutMode: 'semantic',
                        displayColumnDefOptions: {'mrt-row-actions': {size: isPhone ? 56 : 185, grow: false, header: 'Actions'}},
                        // Maria, 17 October 2026: a tidier table, with a light header and tighter rows.
                        // the Actions column sits over the others, so it needs a solid background or their text shows through
                        muiTableProps: {sx: {minWidth: '0 !important', width: '100%', tableLayout: 'auto'}},
                        muiTableHeadCellProps: ({column}) => ({sx: {'&.MuiTableCell-root': {backgroundColor: '#e3f5f1', backgroundImage: 'none', opacity: 1}, fontWeight: 600, py: 1, lineHeight: 1.25, whiteSpace: 'nowrap', ...headingAlignment(fitStep), ...(isPhone ? phoneColumn(column) : fitColumn(column, fitStep)), ...(isPhone && column.id === 'name' ? {zIndex: 2} : {})}}),
                        muiTableBodyCellProps: ({column}) => ({
                            sx: {
                                // Maria, 18 October 2026: slightly shorter rows
                                py: 0.5,
                                verticalAlign: 'middle',
                                ...(isPhone ? phoneColumn(column) : fitColumn(column, fitStep)),
                                ...(column.id === 'mrt-row-actions' || (isPhone && column.id === 'name') ? {'&.MuiTableCell-root': {backgroundColor: '#fff', backgroundImage: 'none', opacity: 1, ...(isPhone && column.id === 'mrt-row-actions' ? {position: 'static'} : {})}} : {})
                            }
                        }),
                        renderRowActions: ({row}) => (isPhone
                            ? (() => {
                                const actions = getContactActions(row.original).filter(action => action.kind !== 'notificationsSpace');
                                return actions.length > 0 && (
                                    <IconButton
                                        aria-label={`Actions for ${row.original.forename ?? ''} ${row.original.surname ?? ''}`.trim()}
                                        data-testid="contact-actions-menu"
                                        onClick={event => setActionsMenu({anchor: event.currentTarget, contact: row.original})}
                                        sx={{p: 0.5}}
                                    >
                                        <MoreHorizIcon/>
                                    </IconButton>
                                );
                            })()
                            : (
                            <Box display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
                                {getContactActions(row.original).map(action => (action.kind === 'notificationsSpace'
                                    ? <Box key={action.kind} sx={{width: 32, flexShrink: 0}}/>
                                    : action.kind === 'notifications'
                                    ? (
                                        <ContactTooltip key={action.kind} title={action.label}>
                                            <IconButton aria-label={action.label} onClick={action.onClick} sx={{color: '#0DAD95', p: 0.5}} data-testid="contact-notifications">
                                                <MailOutlineIcon sx={{fontSize: 24}}/>
                                            </IconButton>
                                        </ContactTooltip>
                                    )
                                    : action.kind === 'home'
                                    ? (
                                        <ContactTooltip key={action.kind} title={action.label}>
                                            <IconButton aria-label={action.label} onClick={action.onClick} sx={{color: '#0DAD95', p: 0.5}}>
                                                <HomeIcon sx={{fontSize: 26}}/>
                                            </IconButton>
                                        </ContactTooltip>
                                    )
                                    : (
                                        <Button
                                            key={action.kind}
                                            variant="outlined"
                                            size="small"
                                            color={action.kind === 'delete' ? 'error' : 'primary'}
                                            onClick={action.onClick}
                                            sx={{minWidth: 0, px: 1.25, py: 0.25, fontSize: 13, whiteSpace: 'nowrap'}}
                                        >
                                            {action.label}
                                        </Button>
                                    )))}
                            </Box>
                        ))
                    }}
                />
                <Menu anchorEl={actionsMenu.anchor} open={Boolean(actionsMenu.anchor)} onClose={closeActionsMenu}>
                    {actionsMenu.contact && getContactActions(actionsMenu.contact)
                        .filter(action => action.kind !== 'notificationsSpace')
                        .map(action => (
                            <MenuItem
                                key={action.kind}
                                sx={action.kind === 'delete' ? {color: 'error.main'} : {}}
                                onClick={() => {
                                    closeActionsMenu();
                                    action.onClick();
                                }}
                            >
                                {action.label}
                            </MenuItem>
                        ))}
                </Menu>
            {/* Maria, 18 October 2026: Add Contact and Confirm Contact Details under the table. Adding, editing or
                deleting a contact does not confirm the section; only Confirm Contact Details does. */}
            <Stack marginTop={1.5} flexDirection={{xs: 'column', sm: 'row'}} alignItems={{xs: 'stretch', sm: 'center'}} gap={1}>
                {!readonly && (
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon/>}
                        onClick={() => setOpenCreateContactDialog(true)}
                    >
                        Add contact
                    </Button>
                )}
                {/* the button is always there, so the section can be confirmed again after a change */}
                {!isEnquiryProfile && (
                    <Button variant="contained" onClick={handleConfirm}>Confirm Contact Details</Button>
                )}
                {!isEnquiryProfile && contacts?.contactsConfirmedAt && (
                    <Typography sx={{display: 'inline-block'}}>
                        Confirmed at {formatDate(contacts.contactsConfirmedAt)} by {contacts?.contactsConfirmedBy}
                    </Typography>
                )}
            </Stack>
                {/* AD-2719: adding a contact asks one question at a time; saving goes through the same checks,
                    confirmation box and request as before. */}
                <AddContactBox
                    open={openCreateContactDialog || openEditContactDialog}
                    mode={openEditContactDialog ? 'edit' : 'add'}
                    existingContact={openEditContactDialog ? selectedContact : null}
                    isParentOwnContact={userHasParentRole}
                    childAddress={toLookupAddress(selectedProfile.basicInfo)}
                    studentName={selectedProfile.name}
                    titles={titles}
                    relations={contactRelations}
                    isParent={userHasParentRole}
                    busy={isAddContactInProgress || isUpdateContactInProgress}
                    validateContactDetails={validateContactPayload}
                    otherChildren={otherChildren}
                    studentId={selectedProfile.id}
                    onSave={payload => (userHasParentRole ? handleSaveContactByParent(payload) : handleSaveContact(payload))}
                    onClose={handleDialogClose}
                />
                <ContactNotificationsBox
                    open={Boolean(notificationsContact)}
                    contact={notificationsContact ?? undefined}
                    studentName={selectedProfile.name}
                    forParent={!user.hasElevatedAccess() && notificationsContact?.userId === user.id}
                    busy={isUpdatePreferencesInProgress}
                    onSave={handleSaveNotifications}
                    onClose={() => setNotificationsContact(null)}
                />
                <EditHomeAddressDialog
                    readonly={readonly}
                    handleDialogClose={handleDialogClose}
                    openEditHomeAddressDialog={openEditHomeAddressDialog}
                    isAddContactInProgress={isAddContactInProgress}
                    isUpdateContactInProgress={isUpdateContactInProgress}
                    handleSaveContactAddress={handleSaveContactAddress}
                    selectedContact={selectedContact}
                />
            </>
    );
};

Contacts.propTypes = {
    readonly: PropTypes.bool
};

export default Contacts;
